import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";
import {
  ADDITIONAL_IMAGE_CAP,
  REGULATORY_DOC_CAP,
  assertCollectorDoc,
  assertCollectorImage,
  uploadCollectorFile,
  uploadCollectorPhoto,
} from "../../helpers/collectorUpload";
import { CollectorPieceValues, CollectorRegulatoryDoc } from "./collectorPieceForm";
import McUploadProgress from "./McUploadProgress";
import { HKPImage } from "../../media/HKPImage";
import { collectorPhotoProblem } from "../../media/collectorMediaUpload";
import { collectorLoginPath } from "../../helpers/collectorSession";

type Props = {
  values: CollectorPieceValues;
  disabled?: boolean;
  onChange: (next: CollectorPieceValues) => void;
  /**
   * Change from the latest values. Uploads finish after the person may have typed more, so appending
   * through this keeps those edits; without it, changes are applied to `values` as rendered.
   */
  onUpdate?: (update: (prev: CollectorPieceValues) => CollectorPieceValues) => void;
  onError?: (message: string) => void;
  onBusyChange?: (busy: boolean) => void;
};

const CollectorAddOnFields: React.FC<Props> = ({
  values,
  disabled,
  onChange,
  onUpdate,
  onError,
  onBusyChange,
}) => {
  const history = useHistory();
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [imageProgress, setImageProgress] = useState<{ current: number; total: number; percent: number } | null>(null);
  // Photos added on this screen show from the phone's own copy; saved ones load the small server copy.
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  useEffect(() => () => Object.values(previewsRef.current).forEach((url) => URL.revokeObjectURL(url)), []);

  // Latest values as rendered, for callers that do not pass onUpdate.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const update = (change: (prev: CollectorPieceValues) => CollectorPieceValues) => {
    if (onUpdate) onUpdate(change);
    else onChange(change(valuesRef.current));
  };

  const removeImage = (index: number) => {
    const url = values.additionalImageUrls[index];
    update((prev) => ({ ...prev, additionalImageUrls: prev.additionalImageUrls.filter((_, i) => i !== index) }));
    // Free the phone's copy of a photo added on this screen as soon as it is removed.
    const preview = url ? previews[url] : undefined;
    if (preview && !values.additionalImageUrls.some((other, i) => i !== index && other === url)) {
      URL.revokeObjectURL(preview);
      setPreviews((prev) => {
        const next = { ...prev };
        delete next[url];
        return next;
      });
    }
  };

  const reportUploadError = (err: unknown, fallback: string) => {
    const problem = collectorPhotoProblem(err);
    if (problem.kind === "session" && history) {
      history.push(collectorLoginPath(history.location.pathname + history.location.search));
      return;
    }
    onError?.(problem.kind === "other" ? fallback : problem.message);
  };

  const setBusy = (images: boolean, docs: boolean) => {
    setUploadingImages(images);
    setUploadingDocs(docs);
    onBusyChange?.(images || docs);
  };

  const addImages = async (files: FileList | null) => {
    if (!files?.length || disabled) return;
    const remaining = ADDITIONAL_IMAGE_CAP - values.additionalImageUrls.length;
    if (remaining <= 0) {
      onError?.(`You can add up to ${ADDITIONAL_IMAGE_CAP} additional photos.`);
      return;
    }
    const chosen = Array.from(files).slice(0, remaining);
    // Check every file first, so a wrong file says why in plain words before anything uploads.
    try {
      chosen.forEach((file) => assertCollectorImage(file));
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Please choose a photo.");
      return;
    }
    setBusy(true, uploadingDocs);
    setImageProgress({ current: 1, total: chosen.length, percent: 0 });
    onError?.("");
    const uploaded: string[] = [];
    const fresh: Record<string, string> = {};
    const append = () => {
      if (!uploaded.length) return;
      const added = [...uploaded];
      update((prev) => ({ ...prev, additionalImageUrls: [...prev.additionalImageUrls, ...added] }));
    };
    try {
      for (let i = 0; i < chosen.length; i += 1) {
        const file = chosen[i];
        setImageProgress({ current: i + 1, total: chosen.length, percent: 0 });
        const url = await uploadCollectorPhoto(file, (percent) => {
          setImageProgress({ current: i + 1, total: chosen.length, percent });
        });
        uploaded.push(url);
        fresh[url] = URL.createObjectURL(file);
      }
      append();
    } catch (err: unknown) {
      append();
      reportUploadError(err, "A photo did not upload. Check your internet and try again.");
    } finally {
      setPreviews((prev) => ({ ...prev, ...fresh }));
      setImageProgress(null);
      setBusy(false, uploadingDocs);
    }
  };

  const addDocs = async (files: FileList | null) => {
    if (!files?.length || disabled) return;
    const remaining = REGULATORY_DOC_CAP - values.regulatoryDocs.length;
    if (remaining <= 0) {
      onError?.(`You can add up to ${REGULATORY_DOC_CAP} documents.`);
      return;
    }
    const chosen = Array.from(files).slice(0, remaining);
    try {
      chosen.forEach((file) => assertCollectorDoc(file));
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Use a PDF or an image of the bill or certificate.");
      return;
    }
    setBusy(uploadingImages, true);
    onError?.("");
    const uploaded: CollectorRegulatoryDoc[] = [];
    try {
      for (const file of chosen) {
        const url = await uploadCollectorFile(file);
        uploaded.push({
          url,
          fileName: file.name,
          contentType: file.type,
          uploadedAt: new Date().toISOString(),
        });
      }
      update((prev) => ({ ...prev, regulatoryDocs: [...prev.regulatoryDocs, ...uploaded] }));
    } catch (err: unknown) {
      if (uploaded.length) update((prev) => ({ ...prev, regulatoryDocs: [...prev.regulatoryDocs, ...uploaded] }));
      reportUploadError(err, "A document did not upload. Check your internet and try again.");
    } finally {
      setBusy(uploadingImages, false);
    }
  };

  const extraSlots = ADDITIONAL_IMAGE_CAP - values.additionalImageUrls.length;
  const docSlots = REGULATORY_DOC_CAP - values.regulatoryDocs.length;

  return (
    <div className="mc-media-row mb-3">
      <section className="mc-card mb-0">
        <h2 className="h6 text-uppercase text-muted mb-1">Additional images</h2>
        <p className="small text-muted mb-3">
          Extra photos of this piece. Up to {ADDITIONAL_IMAGE_CAP}. Cover stays required and separate.
        </p>
        <label className="mc-dropzone mc-dropzone-compact d-block" htmlFor="mc-extra-images">
          <input
            id="mc-extra-images"
            type="file"
            accept="image/*"
            multiple
            disabled={disabled || uploadingImages || extraSlots <= 0}
            onChange={(e) => {
              addImages(e.target.files);
              e.target.value = "";
            }}
          />
          {uploadingImages ? (
            <McUploadProgress
              percent={imageProgress?.percent ?? 0}
              label={
                imageProgress && imageProgress.total > 1
                  ? `Uploading photo ${imageProgress.current} of ${imageProgress.total} · ${imageProgress.percent}%`
                  : `Uploading photo… ${imageProgress?.percent ?? 0}%`
              }
            />
          ) : extraSlots <= 0 ? (
            <span>Maximum of {ADDITIONAL_IMAGE_CAP} reached.</span>
          ) : (
            <span>Add photos ({values.additionalImageUrls.length}/{ADDITIONAL_IMAGE_CAP})</span>
          )}
        </label>
        {values.additionalImageUrls.length > 0 && (
          <div className="mc-thumb-grid mt-3">
            {values.additionalImageUrls.map((url, index) => (
              <div key={`${url}-${index}`} className="mc-thumb">
                <HKPImage allowMaster={false} preset="THUMBNAIL" localPreviewUrl={previews[url]} legacyUrl={url} alt="" />
                <button
                  type="button"
                  className="mc-thumb-remove"
                  disabled={disabled}
                  aria-label={`Remove photo ${index + 1}`}
                  onClick={() => removeImage(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mc-card mb-0">
        <h2 className="h6 text-uppercase text-muted mb-1">Add-on regulatory docs</h2>
        <p className="small text-muted mb-3">
          Bills or certificates. <strong>Only you can see these</strong> — never on the public QR page.
        </p>
        <label className="mc-dropzone mc-dropzone-compact d-block" htmlFor="mc-reg-docs">
          <input
            id="mc-reg-docs"
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp"
            multiple
            disabled={disabled || uploadingDocs || docSlots <= 0}
            onChange={(e) => {
              addDocs(e.target.files);
              e.target.value = "";
            }}
          />
          {uploadingDocs ? (
            <span><Spinner size="sm" /> Uploading…</span>
          ) : docSlots <= 0 ? (
            <span>Maximum of {REGULATORY_DOC_CAP} reached.</span>
          ) : (
            <span>Upload PDF or photo ({values.regulatoryDocs.length}/{REGULATORY_DOC_CAP})</span>
          )}
        </label>
        {values.regulatoryDocs.length > 0 && (
          <ul className="mc-doc-list mt-3 mb-0">
            {values.regulatoryDocs.map((doc, index) => (
              <li key={`${doc.url}-${index}`}>
                <a href={doc.url} target="_blank" rel="noreferrer">
                  {doc.fileName || "Document"}
                </a>
                <button
                  type="button"
                  className="mc-btn-danger-text"
                  disabled={disabled}
                  onClick={() =>
                    update((prev) => ({ ...prev, regulatoryDocs: prev.regulatoryDocs.filter((_, i) => i !== index) }))
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default CollectorAddOnFields;
